import type { ReportChartId, ReportPackageKey, ReportPackageStatus, SolutionChartKey } from '../types';

const MAX_SHOWN_WARNINGS = 6;

export const REPORT_PACKAGE_ORDER: ReportPackageKey[] = ['assessment', 'mitigation', 'operational', 'ex_post'];

export const reportPackageLabels: Record<ReportPackageKey, string> = {
  assessment: 'Soiling Assessment',
  mitigation: 'Mitigation Strategy',
  operational: 'Operational Strategy',
  ex_post: 'Ex-Post Analysis',
};

export const reportPackageStatusLabels: Record<ReportPackageStatus, string> = {
  up_to_date: 'Included',
  stale: 'Outdated',
  missing: 'Not computed',
};

const SOLUTION_CHART_LABELS: Record<SolutionChartKey, string> = {
  npv_heatmap: 'NPV gain heatmap',
  production: 'Production',
  soiling_ratio: 'Soiling ratio',
  capex: 'Cleaning CAPEX',
  opex: 'Cleaning OPEX',
  financial_comparison: 'Financial comparison',
  production_losses: 'Production losses',
  cleaning_schedules: 'Cleaning schedules',
};

/** Readable chart name, `<package>: <chart>`, for the progress and the warnings. */
export const reportChartLabel = (chartId: ReportChartId): string => {
  const [pkg, chart] = chartId.split('.') as [ReportPackageKey, string];
  const chartLabel =
    pkg === 'assessment'
      ? chart === 'soiling_risk'
        ? 'Soiling risk'
        : 'Typical soiling year'
      : SOLUTION_CHART_LABELS[chart as SolutionChartKey];
  return `${reportPackageLabels[pkg]}: ${chartLabel}`;
};

export const reportLabels = {
  export_button: 'Export PDF',
  history_button: 'Report history',
  not_exportable: 'Run at least one analysis to export a report',
  unsaved_changes: 'Save the project before exporting',
  context_loading: 'Checking which results can be exported…',
  context_error: 'Could not check which results can be exported. Hover again to retry.',
  confirm_title: 'Export PDF report',
  confirm_intro: 'The report is generated from the saved project and its up-to-date results.',
  confirm_included: 'Included in the report',
  confirm_flagged: 'Flagged in the report as outdated or not computed',
  confirm_none_flagged: 'All analyses are up to date.',
  confirm_button: 'Generate and download',
  cancel_button: 'Cancel',
  generating: 'Generating the report…',
  preparing_charts: 'Preparing the charts…',
  rendering_charts: (done: number, total: number) => `Rendering charts ${done}/${total}…`,
  chart_unavailable: (chart: string) => `${chart}: no data to draw, chart left out.`,
  chart_render_failed: (chart: string) => `${chart}: the chart could not be drawn and was left out.`,
  chart_too_large: (chart: string) => `${chart}: the chart is too large for the report and was left out.`,
  chart_degraded: (chart: string) => `${chart}: some lines or shapes could not be read and are missing from the chart.`,
  chart_over_budget: (chart: string) =>
    `${chart}: left out, the charts together are too complex for the report (the most complex ones are left out first).`,
  package_results_unreadable: (pkg: string) => `${pkg}: the stored results could not be read, charts left out.`,
  charts_unavailable: 'The charts could not be prepared: the report is generated without them.',
  charts_trimmed: (count: number) => `${count} chart(s) left out to keep the report request under the size limit.`,
  created_with_warnings: (version: number, warnings: string[]) => {
    const shown = warnings.slice(0, MAX_SHOWN_WARNINGS);
    const more = warnings.length - shown.length;
    return [`Report v${version} generated, with remarks:`, ...shown.map((w) => `• ${w}`), ...(more > 0 ? [`…and ${more} more`] : [])].join(
      '\n',
    );
  },
  created: (version: number) => `Report v${version} generated`,
  history_title: 'Reports',
  history_loading: 'Loading reports…',
  history_error: 'The reports could not be loaded.',
  history_empty: 'No report yet',
  history_count: (shown: number, total: number) => `Showing ${shown} of ${total} reports`,
  history_load_more: 'Load more',
  history_load_more_error: 'More reports could not be loaded.',
  retry_button: 'Retry',
  close_button: 'Close',
  column_version: 'Version',
  column_created: 'Created',
  column_author: 'Author',
  column_size: 'Size',
  column_file: 'File',
  column_packages: 'Analyses',
  column_download: 'Download',
  download_button: (version: number) => `Download report v${version}`,
  error_no_up_to_date_results: 'No up-to-date results to export. Run at least one analysis, then retry.',
  error_busy: 'The report service is busy. Please retry later.',
  error_busy_retry_after: (seconds: number) => `The report service is busy. Please retry in ${seconds} s.`,
  error_render_timeout: 'The report took too long to generate. Please retry later, or with fewer analyses or charts.',
  error_archive_failed: 'The report could not be archived. Please retry.',
  error_rendering_failed: 'The report could not be generated. Please contact support if the problem persists.',
  error_too_large: 'The report request is too large.',
  error_invalid_request: 'The report request was rejected as invalid. Please retry; contact support if the problem persists.',
  error_not_found: 'This project or report was not found, or you do not have access to it.',
  error_download_failed: 'The report could not be downloaded. Please retry.',
  error_created_not_downloaded: 'The report was generated but could not be downloaded. Download it from the report history.',
  error_unknown: 'An unexpected error occurred while handling the report.',
};
